import * as THREE from 'three';
import { BLACK, DSTEEL, DWOOD, WOOD, cloth, emis, std, stone } from '../materials.ts';
import { G, M, V, glowSprite, limb } from '../prims.ts';
import { mulberry32 } from '../rand.ts';
import { EMBER } from './land.ts';
import { begin, done, type BaseOpts, type Model } from './types.ts';

/*
 * What the saberes put in the bastion that is not a structure (docs/SABERES.md §4, §6): the lectern of lore that
 * grows by one piece for each saber found, the clay bowls of the lullaby round the keep's foot while the rite holds,
 * and the barracks cat with her kittens.
 *
 * Performance (docs/RENDIMIENTO.md, «Reglas para lo nuevo»): only geometry over shared paints (materials.ts caches
 * by parameters, so the same call is the same material), nothing see-through or two-sided, no lights: what glows is
 * a self-lit paint some other model already has and glow sprites the still bake hands to the shared glow layer. The
 * lectern and the bowls stand still and are baked into a few merged meshes (engine/still.ts); the cat walks, so it
 * is a library model whose poses are baked frames, drawn as instances (no skinning, no matrices of its own).
 */

/** The Vigil's cold white flame (upgrades.ts LEVEL_MATS.vigil): the saberes' own light (docs/SABERES.md §6). */
const PALE = emis('#f2f6ff', '#bcd0ff', 1.4);
const SABER_GLOW = '#e4ecff';
const SLAB = stone('#4a4c53');
const TABLET = stone('#c4c8d0');
const PAGE = std('#e9ecf1', { roughness: 0.9 });
const RED = cloth('#8e1f22');
const CHARCOAL = cloth('#2c3036');
const CLAY = stone('#6b5f58');

/* ---------- the lectern of lore ---------- */

/** The most pieces the lectern shows (one per saber; more saberes than this stack no higher). */
export const LECTERN_MAX = 18;

/** What each saber found adds, in turn: a tablet, a ledger, a mould, a scroll, a reed pipe, a bowl. */
const PIECES = ['tablet', 'ledger', 'mould', 'scroll', 'pipe', 'bowl'] as const;

/** One piece of lore standing on a shelf at (x, y, z) in `p` (faces +Z): `i` picks which and its little turn. */
const piece = (p: THREE.Object3D, kind: (typeof PIECES)[number], x: number, y: number, z: number, i: number): void => {
  const pg = G(p, x, y, z);
  pg.rotation.y = ((i * 37) % 9 - 4) * 0.05;
  switch (kind) {
    case 'tablet': {
      // A pale stone tablet stood on edge, leaning back, a line of the Vigil's light cut in it.
      const t = G(pg, 0, 0, -0.01);
      t.rotation.x = -0.18;
      M(new THREE.BoxGeometry(0.1, 0.13, 0.025), TABLET, t).position.y = 0.065;
      for (const [w, yy] of [[0.06, 0.09], [0.04, 0.06], [0.05, 0.035]] as const) M(new THREE.BoxGeometry(w, 0.008, 0.006), PALE, t).position.set(0, yy, 0.014);
      break;
    }
    case 'ledger': {
      // A thick ledger lying flat, its red cover, its pages' edge, a pale clasp.
      M(new THREE.BoxGeometry(0.12, 0.04, 0.09), i % 2 ? RED : CHARCOAL, pg).position.y = 0.02;
      M(new THREE.BoxGeometry(0.11, 0.03, 0.006), PAGE, pg).position.set(0, 0.02, 0.046);
      M(new THREE.BoxGeometry(0.014, 0.02, 0.008), PALE, pg).position.set(0.04, 0.025, 0.048);
      break;
    }
    case 'mould': {
      // A two-part iron mould, the shape it casts cut in its open half and lit.
      M(new THREE.BoxGeometry(0.1, 0.03, 0.08), DSTEEL, pg).position.y = 0.015;
      M(new THREE.BoxGeometry(0.05, 0.006, 0.026), PALE, pg).position.set(0, 0.031, 0);
      const lid = M(new THREE.BoxGeometry(0.1, 0.03, 0.08), DSTEEL, pg);
      lid.position.set(-0.02, 0.06, -0.035);
      lid.rotation.x = -1.1;
      break;
    }
    case 'scroll': {
      // A rolled scroll on its side, tied in red, a second one leaning on it.
      const s = M(new THREE.CylinderGeometry(0.022, 0.022, 0.13, 7), PAGE, pg);
      s.rotation.z = Math.PI / 2;
      s.position.y = 0.022;
      const tie = M(new THREE.CylinderGeometry(0.024, 0.024, 0.014, 7), RED, pg);
      tie.rotation.z = Math.PI / 2;
      tie.position.y = 0.022;
      const s2 = M(new THREE.CylinderGeometry(0.018, 0.018, 0.11, 7), PAGE, pg);
      s2.position.set(0.02, 0.06, -0.02);
      s2.rotation.set(0.2, 0, 1.0);
      break;
    }
    case 'pipe': {
      // A reed pipe and a bone whistle on a little stand.
      M(new THREE.BoxGeometry(0.1, 0.012, 0.04), DWOOD, pg).position.y = 0.006;
      const r = M(new THREE.CylinderGeometry(0.012, 0.012, 0.12, 6), TABLET, pg);
      r.rotation.z = Math.PI / 2;
      r.position.y = 0.024;
      for (const xx of [-0.03, 0, 0.03]) M(new THREE.BoxGeometry(0.008, 0.004, 0.008), BLACK, pg).position.set(xx, 0.036, 0);
      break;
    }
    default: {
      // A low clay bowl with a pale ember of the Vigil in it.
      M(new THREE.CylinderGeometry(0.05, 0.034, 0.035, 9), CLAY, pg).position.y = 0.0175;
      M(new THREE.CylinderGeometry(0.04, 0.04, 0.006, 9), PALE, pg).position.y = 0.034;
    }
  }
};

/**
 * The lectern of lore («Atril de los saberes», docs/SABERES.md §6): a stand of three stepped timber shelves on a dark
 * stone footing, and a low reading desk in front of it with the open ledger where the garrison wrote down what it knew,
 * the Vigil's cold white light over its pages. Each saber found puts one more piece of lore on the shelves (a tablet,
 * a ledger, a mould, a scroll, a reed pipe, a bowl, by turns), from the lowest step up: `n` of them, up to 18 (the
 * first is the open ledger itself). Built at the keep's scale (engine: placed in a free cell of the keep's yard).
 */
export const lecternTrophy = (o?: BaseOpts & { n?: number }): Model => {
  const n = Math.max(1, Math.min(LECTERN_MAX, Math.floor(o?.n ?? 1)));
  const c = begin(o, 0.8, 61);
  const { g } = c;
  const rng = mulberry32(611);
  // The footing.
  M(new THREE.BoxGeometry(1.0, 0.08, 0.62), SLAB, g).position.set(0, 0.04, 0);
  M(new THREE.BoxGeometry(0.94, 0.03, 0.56), stone('#5f5b55'), g).position.set(0, 0.095, 0);
  // The stand: three stepped shelves rising to the back, so every piece reads from the high table camera, between
  // stepped cheeks, a dark back board with the garrison's red along its top.
  const W = 0.86;
  const tiers: [number, number][] = [[0.16, 0.0], [0.31, -0.15], [0.46, -0.3]];
  const at = G(g, 0, 0.11, 0);
  tiers.forEach(([y, z], j) => {
    M(new THREE.BoxGeometry(W - 0.04, y, 0.15), j % 2 ? WOOD : DWOOD, at).position.set(0, y / 2, z);
    M(new THREE.BoxGeometry(W - 0.04, 0.018, 0.012), DWOOD, at).position.set(0, y - 0.01, z + 0.076);
    for (const k of [-1, 1]) M(new THREE.BoxGeometry(0.04, y + 0.06, 0.15), DWOOD, at).position.set(k * (W / 2), (y + 0.06) / 2, z);
  });
  M(new THREE.BoxGeometry(W + 0.04, 0.7, 0.03), stone('#34302b'), at).position.set(0, 0.35, -0.39);
  M(new THREE.BoxGeometry(W + 0.06, 0.04, 0.05), DWOOD, at).position.set(0, 0.72, -0.39);
  M(new THREE.BoxGeometry(W - 0.1, 0.05, 0.01), RED, at).position.set(0, 0.66, -0.37);
  // The pieces: six to a step, from the lowest up (the first saber is the open ledger on the desk).
  for (let i = 1; i < n; i++) {
    const k = i - 1;
    const row = Math.floor(k / 6);
    const col = k % 6;
    const x = -0.35 + col * 0.14 + (rng() - 0.5) * 0.015;
    const [y, z] = tiers[row] as [number, number];
    piece(at, PIECES[(k + row) % PIECES.length] as (typeof PIECES)[number], x, y, z + 0.01, i);
  }
  if (n > 1) glowSprite(at, SABER_GLOW, 0.7, V(0, (tiers[Math.min(2, Math.floor((n - 2) / 6))] as [number, number])[0] + 0.1, -0.1), 0.06 + 0.005 * n);
  // The reading desk in front, slanted toward whoever reads (and the high camera), the open ledger on it.
  const desk = G(g, 0.0, 0.11, 0.2);
  M(new THREE.BoxGeometry(0.16, 0.03, 0.12), DWOOD, desk).position.y = 0.015;
  M(new THREE.CylinderGeometry(0.035, 0.045, 0.3, 6), DWOOD, desk).position.y = 0.17;
  const top = G(desk, 0, 0.34, 0);
  top.rotation.x = 0.5;
  M(new THREE.BoxGeometry(0.4, 0.03, 0.28), DWOOD, top);
  M(new THREE.BoxGeometry(0.4, 0.04, 0.02), DWOOD, top).position.set(0, 0.03, 0.14);
  for (const k of [-1, 1]) {
    const leaf = M(new THREE.BoxGeometry(0.17, 0.016, 0.22), PAGE, top);
    leaf.position.set(k * 0.088, 0.024, 0);
    leaf.rotation.z = k * -0.1;
    // Lines of the Vigil's light on each page.
    for (let j = 0; j < 4; j++) M(new THREE.BoxGeometry(0.11, 0.004, 0.006), j === 0 ? PALE : CHARCOAL, top).position.set(k * 0.088, 0.034 + Math.abs(k) * 0.004, -0.07 + j * 0.045);
  }
  M(new THREE.BoxGeometry(0.012, 0.03, 0.24), RED, top).position.set(0, 0.03, 0);
  glowSprite(top, SABER_GLOW, 0.5, V(0, 0.1, 0), 0.3);
  return done(c, 1.1);
};

/* ---------- the lullaby ---------- */

/**
 * The lullaby (docs/SABERES.md §4.3 «Arrullo»): while the rite holds, eight wide clay bowls stand round the keep's
 * foot on a ring of ash drawn on the ground, its line glowing ember-dull between them; in each bowl a heap of coals,
 * a tongue of fire and a tall thread of smoke, so the rite reads from the table camera. In the keep's frame and units
 * (the keep's walls are 0.8 wide, its plinth 1.26 at most): the ring stands clear of the plinth's corners.
 */
export const lullabyBowls = (o?: BaseOpts): Model => {
  const c = begin(o, 0.8, 62);
  const { g } = c;
  const rng = mulberry32(621);
  const N = 8;
  const R = 1.02;
  // Pale ash-grey smoke: dark puffs read as beads against the ground.
  const SMOKE = stone('#77736c');
  const FIRE = emis('#ffb070', '#ff5a10', 1.4);
  // The ring on the ground: a dark band of ash with a dull ember line along it.
  const ash = M(new THREE.TorusGeometry(R, 0.05, 3, 48), stone('#2b2725'), g);
  ash.rotation.x = Math.PI / 2;
  ash.scale.z = 0.18;
  ash.position.y = 0.006;
  const line = M(new THREE.TorusGeometry(R, 0.016, 3, 48), EMBER, g);
  line.rotation.x = Math.PI / 2;
  line.scale.z = 0.4;
  line.position.y = 0.012;
  for (let i = 0; i < N; i++) {
    const a = (i / N) * Math.PI * 2 + Math.PI / N;
    const b = G(g, Math.sin(a) * R, 0, Math.cos(a) * R);
    b.rotation.y = rng() * 3;
    M(new THREE.CylinderGeometry(0.15, 0.1, 0.09, 9), CLAY, b).position.y = 0.045;
    M(new THREE.CylinderGeometry(0.13, 0.13, 0.01, 9), EMBER, b).position.y = 0.088;
    for (let k = 0; k < 4; k++) M(new THREE.DodecahedronGeometry(0.035, 0), FIRE, b).position.set(Math.sin(k * 1.6) * 0.06, 0.1, Math.cos(k * 1.6) * 0.06);
    // A tongue of fire, and the smoke rising high and leaning with the wind.
    const tongue = M(new THREE.ConeGeometry(0.045, 0.16, 6), FIRE, b);
    tongue.position.y = 0.18;
    for (const [dx, dy, sz] of [[0.0, 0.32, 0.04], [0.02, 0.44, 0.05], [0.05, 0.57, 0.06], [0.09, 0.7, 0.065]] as const) M(new THREE.DodecahedronGeometry(sz, 0), SMOKE, b).position.set(dx, dy, dx * 0.3);
    glowSprite(b, '#ff8a3a', 0.6, V(0, 0.16, 0), 0.55);
  }
  // A warm breath of light on the ground under the ring.
  glowSprite(g, '#ff7a2a', 3.4, V(0, 0.05, 0), 0.12);
  return done(c, 0.8);
};

/* ---------- the barracks cat ---------- */

/** The cat's poses, in the order of its baked frames: four steps of its walk, sitting, asleep curled, a leap. */
export const CAT_POSES = ['walkA', 'walkB', 'walkC', 'walkD', 'sit', 'sleep', 'leap'] as const;
export type CatPose = (typeof CAT_POSES)[number];

export interface CatModel extends Model {
  pose(p: CatPose): void;
}

// A dark slate grey with a near-white chest: she has to stand out on pale stone and dark ground alike.
const FUR = std('#565b64', { roughness: 0.95, flatShading: true });
const FUR_DK = std('#33373e', { roughness: 0.95, flatShading: true });
const FUR_LT = std('#d3d6db', { roughness: 0.95, flatShading: true });

/**
 * The barracks cat (docs/SABERES.md §4.5): a lean dark-grey tabby, darker bars down her back, a pale chest and muzzle,
 * ears up, a long tail. Faces +Z, about 0.5 long and 0.32 to the ears. Posed by `pose` (its frames are baked:
 * engine/library.ts `cat`): a four-step walk, sitting up with her tail round her feet, curled asleep, a leap.
 * The kittens are the same figure drawn smaller.
 */
export const barracksCat = (o?: BaseOpts): CatModel => {
  const c = begin(o, 0.3, 63);
  const { g } = c;
  // The body pivots at the hips so she can sit up.
  const hips = G(g, 0, 0.15, -0.1);
  const body = G(hips, 0, 0, 0.1);
  const torso = M(new THREE.SphereGeometry(0.1, 9, 6), FUR, body);
  torso.scale.set(0.72, 0.68, 1.55);
  const chest = M(new THREE.SphereGeometry(0.07, 8, 5), FUR_LT, body);
  chest.position.set(0, -0.025, 0.1);
  chest.scale.set(0.9, 0.9, 1.0);
  for (const z of [-0.08, -0.02, 0.04]) {
    const bar = M(new THREE.BoxGeometry(0.11, 0.02, 0.022), FUR_DK, body);
    bar.position.set(0, 0.062, z);
  }
  // Head on the neck, ears, muzzle.
  const head = G(body, 0, 0.08, 0.15);
  M(new THREE.SphereGeometry(0.068, 9, 6), FUR, head).scale.set(1, 0.92, 0.95);
  const muzzle = M(new THREE.SphereGeometry(0.034, 7, 5), FUR_LT, head);
  muzzle.position.set(0, -0.02, 0.05);
  muzzle.scale.set(1.2, 0.8, 0.9);
  M(new THREE.SphereGeometry(0.008, 5, 3), BLACK, head).position.set(0, -0.006, 0.083);
  for (const k of [-1, 1]) {
    const ear = M(new THREE.ConeGeometry(0.026, 0.06, 4), FUR, head);
    ear.position.set(k * 0.038, 0.06, 0.0);
    ear.rotation.set(-0.1, 0, k * -0.25);
    M(new THREE.BoxGeometry(0.02, 0.012, 0.008), FUR_DK, head).position.set(k * 0.03, 0.035, 0.06);
  }
  // Legs: each a pivot at the shoulder or the hip with the leg hanging under it.
  const leg = (x: number, z: number, len: number): THREE.Group => {
    const p = G(body, x, -0.03, z);
    limb(p, V(0, 0, 0), V(0, -len, 0), 0.024, 0.019, FUR, 5);
    M(new THREE.SphereGeometry(0.02, 5, 3), FUR_LT, p).position.set(0, -len, 0.008);
    return p;
  };
  const fl = leg(-0.04, 0.1, 0.1), fr = leg(0.04, 0.1, 0.1), bl = leg(-0.045, -0.1, 0.1), br = leg(0.045, -0.1, 0.1);
  // The tail: three pieces from the rump, held up in a soft curve.
  const tail = G(body, 0, 0.02, -0.15);
  const t2 = G(tail, 0, 0, 0);
  limb(t2, V(0, 0, 0), V(0, 0.06, -0.08), 0.018, 0.015, FUR, 5);
  const t3 = G(t2, 0, 0.06, -0.08);
  limb(t3, V(0, 0, 0), V(0, 0.09, -0.04), 0.015, 0.012, FUR, 5);
  const tip = G(t3, 0, 0.09, -0.04);
  limb(tip, V(0, 0, 0), V(0, 0.05, 0.03), 0.012, 0.009, FUR_DK, 5);

  const legs = [fl, fr, bl, br];
  const pose = (p: CatPose): void => {
    hips.position.set(0, 0.15, -0.1);
    hips.rotation.set(0, 0, 0);
    body.rotation.set(0, 0, 0);
    head.rotation.set(0, 0, 0);
    head.position.set(0, 0.08, 0.15);
    for (const l of legs) {
      l.rotation.set(0, 0, 0);
      l.scale.set(1, 1, 1);
      l.visible = true;
    }
    t2.rotation.set(0, 0, 0);
    t3.rotation.set(0, 0, 0);
    tip.rotation.set(0, 0, 0);
    torso.scale.set(0.72, 0.68, 1.55);
    if (p.startsWith('walk')) {
      // Diagonal pairs swing together; the tail sways the other way.
      const ph = CAT_POSES.indexOf(p) * (Math.PI / 2);
      const s = Math.sin(ph) * 0.45;
      fl.rotation.x = s;
      br.rotation.x = s;
      fr.rotation.x = -s;
      bl.rotation.x = -s;
      hips.position.y = 0.15 + Math.abs(Math.cos(ph)) * 0.008;
      head.rotation.x = 0.08;
      t2.rotation.z = Math.sin(ph) * 0.25;
      t3.rotation.x = 0.2;
    } else if (p === 'sit') {
      // Up on her front legs, the hind folded under, head up, the tail wrapped round her feet on the ground.
      hips.position.y = 0.06;
      hips.rotation.x = -0.75;
      bl.rotation.x = 1.3;
      br.rotation.x = 1.3;
      bl.scale.y = 0.6;
      br.scale.y = 0.6;
      fl.rotation.x = 0.75;
      fr.rotation.x = 0.75;
      fl.scale.y = 1.25;
      fr.scale.y = 1.25;
      head.rotation.x = 0.7;
      t2.rotation.set(-1.9, 0.2, 0);
      t3.rotation.set(-0.5, 1.2, 0);
      tip.rotation.set(0, 0.6, 0);
    } else if (p === 'sleep') {
      // Curled: the body low and round, the head on the tail, the legs tucked away.
      hips.position.y = 0.06;
      torso.scale.set(1.0, 0.55, 1.25);
      for (const l of legs) l.visible = false;
      head.position.set(0.06, -0.01, 0.12);
      head.rotation.set(0.5, 0.9, 0.25);
      body.rotation.y = 0.3;
      t2.rotation.set(-1.6, -0.9, 0);
      t3.rotation.set(-0.2, -1.4, 0);
      tip.rotation.set(0, -0.8, 0);
    } else {
      // A leap: stretched out, the fore legs reaching, the hind pushing off.
      hips.rotation.x = -0.25;
      fl.rotation.x = -1.0;
      fr.rotation.x = -0.9;
      bl.rotation.x = 0.9;
      br.rotation.x = 1.0;
      torso.scale.set(0.66, 0.62, 1.75);
      t3.rotation.x = 0.9;
    }
  };
  pose('walkA');
  const m = done(c, 0.34) as CatModel;
  m.pose = pose;
  return m;
};
